// src/pages/dashboard/nanny/contracts/CurrentContract.jsx
import { useState, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import api from '../../../../lib/api';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { formatMoney, formatDateOnly } from '../../../../lib/helpers';
import { 
  Briefcase, 
  Calendar, 
  Home, 
  Phone, 
  ChevronLeft, 
  ChevronRight 
} from 'lucide-react';
import RatingDisplay from '../../../../components/common/RatingDisplay';
import RateUser from '../../../../components/common/RateUser';

export default function NannyCurrentContracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    api.get('/contracts/current')
      .then(res => setContracts(res.data.data || []))
      .catch(() => setContracts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    onSelect();
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();
  const scrollTo = (index) => emblaApi?.scrollTo(index);

  if (loading) return <LoadingSpinner size="lg" text="Loading your contracts..." />;

  if (!contracts.length) {
    return (
      <div className="text-center py-32">
        <Briefcase size={140} className="mx-auto text-gray-300 mb-10 opacity-40" />
        <h2 className="text-5xl font-bold text-gray-700">No Active Job</h2>
        <p className="text-2xl text-gray-500 mt-6">You are not currently employed</p>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-6xl font-bold text-gray-900">You Are Employed!</h1>
          <p className="text-2xl sm:text-3xl text-gray-600 mt-6">Working with trusted employers</p>
        </div>

        <div className="relative">
          {/* Carousel */}
          <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
            <div className="flex">
              {contracts.map((contract) => (
                <div key={contract.id} className="flex-none w-full min-w-0 px-4 sm:px-8">
                  <div className="space-y-10 sm:space-y-12">
                    {/* Start Date Banner */}
                    <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-3xl p-10 sm:p-16 text-white text-center shadow-2xl">
                      <Calendar size={70} className="mx-auto mb-6 sm:mb-8" />
                      <p className="text-2xl sm:text-4xl font-bold">Employment Started</p>
                      <p className="text-4xl sm:text-6xl font-black mt-4 sm:mt-6">{formatDateOnly(contract.start_date)}</p>
                    </div>

                    {/* Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                      {/* Employer Card */}
                      <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 border-2 border-gray-100 hover:shadow-3xl transition">
                        <h3 className="text-3xl sm:text-4xl font-bold mb-8 sm:mb-10 flex items-center gap-5">
                          <Home className="text-primary" size={40} />
                          Your Employer
                        </h3>
                        <div className="flex flex-col sm:flex-row items-center gap-8 text-center sm:text-left">
                          <div className="w-32 h-32 sm:w-36 sm:h-36 bg-gradient-to-br from-secondary to-primary rounded-3xl flex items-center justify-center text-white text-6xl font-black shadow-2xl overflow-hidden flex-shrink-0">
                            { contract.customer.profile_picture && !contract.customer.profile_picture.includes('default-avatar') ? 
                              <img
                                src={process.env.REACT_APP_IMAGE_URL+contract.customer?.profile_picture}
                                alt={`${contract.customer.full_name}`}
                                className="w-full h-full object-cover rounded-3xl"
                              /> : 
                              contract.customer?.full_name?.[0]?.toUpperCase() || 'U'
                            }
                          </div>
                          <div className="flex-1">
                            <p className="text-2xl sm:text-4xl font-bold text-gray-900">
                              {contract.customer?.full_name}
                            </p>
                            <div className="my-4"><RatingDisplay rating={contract?.customer?.rating} /></div>                        
                            <p className="text-xl sm:text-2xl text-gray-600 mt-4 sm:mt-5 flex items-center justify-center sm:justify-start gap-4">
                              <Phone size={28} /> {contract.customer?.phone_number}
                            </p>
                            <div className="mt-6">
                              <RateUser userId={contract.customer?.id} />
                            </div> 
                          </div>
                        </div>
                      </div>

                      {/* Salary Card */}
                      <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 border-2 border-gray-100 hover:shadow-3xl transition">
                        <h3 className="text-3xl sm:text-4xl font-bold mb-8 sm:mb-10">Your Salary</h3>
                        <div className="text-center py-12 sm:py-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-3xl">
                          <p className="text-5xl sm:text-7xl font-black text-green-700">
                            {formatMoney(contract.monthly_salary)}
                          </p>
                          <p className="text-2xl sm:text-3xl text-gray-700 mt-6">monthly salary</p>
                        </div>
                        <p className="text-lg sm:text-xl text-gray-700 mt-8 sm:mt-10 leading-relaxed whitespace-pre-line">
                          {contract.terms || 'Standard working conditions apply.'}
                        </p>
                      </div>
                    </div>

                    {/* Motivation Card */}
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-3xl p-12 sm:p-16 text-white text-center shadow-2xl">
                      <h3 className="text-4xl sm:text-5xl font-bold mb-6">Keep being excellent!</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          {contracts.length > 1 && (
            <>
              <button
                onClick={scrollPrev}
                disabled={selectedIndex === 0}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white rounded-full p-4 sm:p-6 shadow-2xl transition disabled:opacity-30"
              >
                <ChevronLeft size={40} sm={60} />
              </button>

              <button
                onClick={scrollNext}
                disabled={selectedIndex === contracts.length - 1}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white rounded-full p-4 sm:p-6 shadow-2xl transition disabled:opacity-30"
              >
                <ChevronRight size={40} sm={60} />
              </button>
            </>
          )}
        </div>

        {/* Dots Indicator */}
        {contracts.length > 1 && (
          <div className="flex justify-center gap-4 sm:gap-5 mt-16 sm:mt-20">
            {contracts.map((_, idx) => (
              <button
                key={idx}
                onClick={() => scrollTo(idx)}
                className={`h-4 sm:h-5 rounded-full transition-all duration-300 ${
                  idx === selectedIndex
                    ? 'bg-primary w-16 sm:w-24'
                    : 'bg-gray-300 w-4 sm:w-5 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}